import { CheckCircleIcon, ExclamationCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import type { ToastId } from "@highoutput/ui-components";
import { Box, chakra, Flex, Icon, Text, useToast } from "@highoutput/ui-components";
import * as React from "react";
import type Nullable from "~/types/nullable";

type Variant = "success" | "danger";

interface ToastConfig {
  /** custom title */
  title?: Nullable<string | JSX.Element>;
  variant: Variant;
  duration: number;
  callback: () => void;
}

export default function useNotify() {
  const toast = useToast();
  const toastRef = React.useRef<ToastId>();

  const closeCurrentToast = () => {
    if (toastRef.current) toast.close(toastRef.current);
  };

  return (
    message: string | JSX.Element,
    { title, variant = "success", duration = 3000, callback }: Partial<ToastConfig> = {},
  ) => {
    closeCurrentToast();

    const borderColor: Record<Variant, string> = {
      success: "success.600",
      danger: "error.600",
    };

    const bgColor: Record<Variant, string> = {
      success: "success.500",
      danger: "error.500",
    };

    const heading: Record<Variant, string> = {
      success: "Success!",
      danger: "Error!",
    };

    const headingColor: Record<Variant, string> = {
      success: "success.900",
      danger: "error.900",
    };

    const messageColor: Record<Variant, string> = {
      success: "success.800",
      danger: "error.900",
    };

    const iconColor: Record<Variant, string> = {
      success: "success.800",
      danger: "error.800",
    };

    const closeButtonColor: Record<Variant, string> = {
      success: "success.700",
      danger: "#F04438",
    };

    const icon = {
      success: CheckCircleIcon,
      danger: ExclamationCircleIcon,
    };

    toastRef.current = toast({
      position: "top",
      duration: duration,
      onCloseComplete: callback,
      containerStyle: {
        marginTop: "48px",
      },
      render: () => (
        <Flex
          padding={4}
          gap="12px"
          rounded="8px"
          alignItems="start"
          border="1px solid"
          bgColor={bgColor[variant]}
          borderColor={borderColor[variant]}
          maxWidth="606px"
          width="full"
        >
          <Icon as={icon[variant]} width="20px" height="20px" stroke={iconColor[variant]} />

          <Box fontFamily="'Inter', sans-serif" flexGrow={1} fontSize="sm" lineHeight="20px">
            <Text color={headingColor[variant]} fontWeight="medium">
              {title ?? heading[variant]}
            </Text>

            <Text color={messageColor[variant]}>{message}</Text>
          </Box>
          <chakra.button
            color={closeButtonColor[variant]}
            onClick={closeCurrentToast}
            display="flex"
            aria-label="Close"
          >
            <Icon as={XMarkIcon} width="20px" height="20px" fill={iconColor[variant]} />
          </chakra.button>
        </Flex>
      ),
    });
  };
}
